import { defineStore } from 'pinia';
import { useAuthenticationStore } from './authentication';
import { ref, computed, watchEffect } from 'vue';
import { AuthContext } from '@precon-tools-suite-by-shared/shared-auth-js-ability-checks';
import {
    TokenClaims,
    extraPermissionsFromClaims,
    rolesFromClaims,
} from '../helpers/interpret-claims';
import type {
    Role,
    Permission,
} from '@precon-tools-suite-by-shared/js-precon-rbac';
import { useLazyQuery } from '@vue/apollo-composable';
import { RolesClaimFallbackDocument } from './authorization.generated';

export const useAuthorizationStore = defineStore('precon-authorization', () => {
    const authentication = useAuthenticationStore();

    // Fetching roles.
    const fetchIfRolesClaimMissing = ref<boolean>(false);

    const idTokenRoles = computed<Role[] | null>(() =>
        rolesFromClaims(authentication.idTokenClaims as TokenClaims)
    );

    const fetchRoles = computed<boolean>(
        () =>
            !authentication.loading &&
            authentication.isAuthenticated &&
            idTokenRoles.value === null &&
            fetchIfRolesClaimMissing.value
    );

    const {
        result: rolesResult,
        loading: rolesLoading,
        load,
    } = useLazyQuery(RolesClaimFallbackDocument, {}, () => ({
        enabled: fetchRoles.value,
    }));

    watchEffect(() => {
        if (fetchRoles.value) load();
    });

    const roles = computed<Role[]>(() => {
        if (idTokenRoles.value) return idTokenRoles.value;
        return (rolesResult.value?.me?.roles ?? []) as unknown as Role[];
    });

    // Auth context,

    const internalAuthContext = ref<AuthContext | null>(null);

    const authContext = computed({
        get: () =>
            internalAuthContext.value ??
            new AuthContext({
                teamId: authentication.teamId,
                userId: authentication.userId,
                roles: roles.value,
                extraPermissions: extraPermissionsFromClaims(
                    authentication.idTokenClaims
                ),
            }),
        set: (newValue) => (internalAuthContext.value = newValue),
    });

    const permissions = computed<ReadonlySet<Permission>>(() =>
        authContext.value?.getPermissions()
    );

    const loading = computed(
        () => !!authentication.loading || !!rolesLoading.value
    );

    const userId = computed(() => authContext.value.userId);
    const teamId = computed(() => authContext.value.teamId);
    const isSuperUser = computed(() => authContext.value.isSuperUser);

    return {
        authContext,
        fetchIfRolesClaimMissing,
        loading,
        roles,
        permissions,
        teamId,
        userId,
        isSuperUser,
    };
});
